import { useEffect, useState, useCallback, useRef } from 'react';
import { Drawer } from './ui';

// T040 — Tiny toast system. Stacked bottom-right, auto-dismiss after 5s,
// dismissable via × button, optional Undo action (T043 uses this).
//
// Usage pattern (no React Context — module-level subscription):
//   import { toast } from './components/Toasts.jsx';
//   toast('Scan triggered');                         // info-level
//   toast('Server returned 401', { kind: 'error' }); // red border
//   toast('Dismissed', { action: { label: 'Undo', onClick: () => … } });
//
// Mount <ToastHost /> ONCE near the root (App.jsx) — every toast()
// call propagates to it via the module-level listener Set.
//
// T077 — Toast history. Every toast() call also appends a frozen record
// to an in-memory ring buffer (capped at HISTORY_LIMIT). The
// `<ToastHistoryButton />` opens a drawer that lists the last N toasts
// so the operator can re-read a status message that vanished too fast.
// History persists for the SPA session; refresh clears it (we don't put
// notification noise in localStorage).

const _listeners = new Set();
const _historyListeners = new Set();
let _seq = 0;

const HISTORY_LIMIT = 50;
const _history = []; // newest-first

// Public API — call from anywhere in the app.
export function toast(message, opts = {}) {
  const id = ++_seq;
  const t = {
    id,
    message: String(message ?? ''),
    kind: opts.kind ?? 'info', // 'info' | 'error' | 'success'
    durationMs: opts.durationMs ?? (opts.action ? 8000 : 5000),
    action: opts.action ?? null, // {label, onClick}
  };
  _listeners.forEach((fn) => fn({ type: 'add', toast: t }));

  // T077 — record into the history ring buffer. Strip the action handler
  // (closures don't survive re-renders meaningfully; the drawer only
  // shows the label as a hint that an Undo *was* available).
  _history.unshift({
    id: t.id,
    message: t.message,
    kind: t.kind,
    actionLabel: t.action?.label ?? null,
    at: Date.now(),
  });
  if (_history.length > HISTORY_LIMIT) _history.length = HISTORY_LIMIT;
  _historyListeners.forEach((fn) => fn(_history.slice()));
  return id;
}

export function dismissToast(id) {
  _listeners.forEach((fn) => fn({ type: 'dismiss', id }));
}

// T077 — read-only accessors for the history drawer.
export function getToastHistory() {
  return _history.slice();
}
export function clearToastHistory() {
  _history.length = 0;
  _historyListeners.forEach((fn) => fn([]));
}
export function subscribeToastHistory(fn) {
  _historyListeners.add(fn);
  return () => _historyListeners.delete(fn);
}

// Host component — render once near the root.
export function ToastHost() {
  const [toasts, setToasts] = useState([]);
  const timersRef = useRef(new Map());

  useEffect(() => {
    const handler = (event) => {
      if (event.type === 'add') {
        setToasts((cur) => [...cur, event.toast]);
        if (event.toast.durationMs > 0) {
          const tid = setTimeout(() => {
            setToasts((cur) => cur.filter((x) => x.id !== event.toast.id));
            timersRef.current.delete(event.toast.id);
          }, event.toast.durationMs);
          timersRef.current.set(event.toast.id, tid);
        }
      } else if (event.type === 'dismiss') {
        setToasts((cur) => cur.filter((x) => x.id !== event.id));
        const tid = timersRef.current.get(event.id);
        if (tid) {
          clearTimeout(tid);
          timersRef.current.delete(event.id);
        }
      }
    };
    // Copied here, in the effect body, not in the cleanup: React can swap a
    // ref between the two, and reading `.current` at teardown is the shape the
    // exhaustive-deps rule warns about. This particular ref holds a Map made
    // once by useRef and never reassigned, so the two are the same object — but
    // the copy costs nothing and keeps the guarantee local instead of assumed.
    const timers = timersRef.current;
    _listeners.add(handler);
    return () => {
      _listeners.delete(handler);
      // Drain any remaining timers.
      //
      for (const tid of timers.values()) clearTimeout(tid);
      timers.clear();
    };
  }, []);

  const close = useCallback((id) => dismissToast(id), []);

  // T250 — do NOT early-return before the live region. A polite region has to
  // be in the DOM *before* content lands in it; mounting it together with the
  // first toast means assistive tech often announces nothing at all. The region
  // is always present and empty; only the toasts inside it come and go.

  return (
    <div
      aria-live="polite"
      aria-atomic="true"
      className="pointer-events-none fixed bottom-4 right-4 z-50 flex flex-col gap-2"
    >
      {toasts.map((t) => (
        <ToastItem key={t.id} t={t} onClose={() => close(t.id)} />
      ))}
    </div>
  );
}

function ToastItem({ t, onClose }) {
  const ring =
    t.kind === 'error'
      ? 'border-red-700 bg-red-950/80 text-red-100'
      : t.kind === 'success'
        ? 'border-emerald-700 bg-emerald-950/80 text-emerald-100'
        : 'border-slate-700 bg-slate-900/90 text-slate-100';
  return (
    <div
      role={t.kind === 'error' ? 'alert' : 'status'}
      className={`pointer-events-auto flex min-w-[260px] max-w-md items-start gap-3 rounded-xl border p-3 text-sm shadow-lg backdrop-blur ${ring}`}
    >
      <span className="flex-1 leading-snug">{t.message}</span>
      {t.action && (
        <button
          type="button"
          onClick={() => {
            t.action.onClick?.();
            onClose();
          }}
          className="rounded border border-current/30 px-2 py-0.5 text-xs font-medium hover:bg-current/10"
        >
          {t.action.label}
        </button>
      )}
      <button
        type="button"
        onClick={onClose}
        aria-label="Dismiss"
        className="shrink-0 text-current/60 hover:text-current"
      >
        ×
      </button>
    </div>
  );
}

// T077 — History drawer toggle + the drawer itself. Mounted inside the
// sidebar so it lives at the same depth as the other nav items.
export function ToastHistoryButton() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(() => getToastHistory());

  useEffect(() => {
    const unsub = subscribeToastHistory((next) => setItems(next));
    return unsub;
  }, []);

  const unread = items.length;
  const errors = items.filter((x) => x.kind === 'error').length;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Open notification history"
        className="relative flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm text-slate-400 transition hover:bg-slate-800/60 hover:text-slate-200"
      >
        <span className="w-4 text-xs">⌖</span>
        <span>History</span>
        {unread > 0 && (
          <span
            className={
              'ml-auto rounded-full px-1.5 py-0.5 text-[10px] ' +
              (errors > 0 ? 'bg-red-900/40 text-red-300' : 'bg-slate-700 text-slate-300')
            }
            title={errors > 0 ? `${errors} error${errors === 1 ? '' : 's'}` : ''}
          >
            {unread}
          </span>
        )}
      </button>

      {open && (
        <Drawer
          open
          onClose={() => setOpen(false)}
          side="right"
          panelClassName="w-96 max-w-full"
          scrimClassName="bg-slate-950/50 backdrop-blur-[2px]"
          ariaLabel="Notification history"
        >
            <div className="flex items-center justify-between border-b border-slate-800 px-4 py-3">
              <div>
                <p className="text-sm font-semibold text-slate-100">Notification history</p>
                <p className="text-xs text-slate-500">Last {HISTORY_LIMIT} · session-only, refresh clears</p>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close drawer"
                className="text-slate-300 hover:text-slate-100"
              >
                ×
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              {items.length === 0 ? (
                <div className="px-4 py-8 text-center text-xs text-slate-500">
                  Nothing yet. Toasts you trigger this session will show up here.
                </div>
              ) : (
                <ul className="divide-y divide-slate-800">
                  {items.map((it) => (
                    <li key={it.id} className="px-4 py-3 text-sm">
                      <div className="flex items-baseline justify-between gap-2">
                        <span className={
                          'shrink-0 text-[10px] uppercase tracking-widest ' +
                          (it.kind === 'error' ? 'text-red-400' : it.kind === 'success' ? 'text-emerald-400' : 'text-slate-500')
                        }>
                          {it.kind}
                        </span>
                        <span className="shrink-0 font-mono text-[10px] text-slate-600">
                          {formatTime(it.at)}
                        </span>
                      </div>
                      <p className={
                        'mt-1 leading-snug ' +
                        (it.kind === 'error' ? 'text-red-200' : it.kind === 'success' ? 'text-emerald-200' : 'text-slate-200')
                      }>
                        {it.message}
                      </p>
                      {it.actionLabel && (
                        <p className="mt-0.5 text-xs text-slate-500">
                          had action: <em>{it.actionLabel}</em> (expired)
                        </p>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {items.length > 0 && (
              <div className="border-t border-slate-800 px-4 py-2">
                <button
                  type="button"
                  onClick={clearToastHistory}
                  className="text-[11px] text-slate-500 underline decoration-dotted hover:text-slate-300"
                >
                  Clear history
                </button>
              </div>
            )}
        </Drawer>
      )}
    </>
  );
}

function formatTime(ts) {
  const d = new Date(ts);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}
